import MuiTooltip from '@mui/material/Tooltip'
import type { SxProps, Theme } from '@mui/material/styles'
import { useId, useMemo } from 'react'

import { cn, focusRing, targetSize } from './cn'
import { Info } from './icons'

export interface InfoTipProps {
  /** The button's accessible name, such as "About the Skillable account". */
  readonly label: string
  /** What the tooltip says. */
  readonly text: string
  readonly className?: string
}

// design.md's Tooltip: soft-grey surface, primary text, default corners, 4px 8px padding, the
// tooltip-label type (12px, 500, 160%) and no arrow. MUI's styles win over Tailwind classes.
const tooltipSx: SxProps<Theme> = (theme) => ({
  bgcolor: (theme.vars ?? theme).palette.softGrey.main,
  color: (theme.vars ?? theme).palette.text.primary,
  borderRadius: 1,
  padding: '4px 8px',
  fontSize: '0.75rem',
  fontWeight: 500,
  lineHeight: 1.6,
})

/**
 * The small info icon beside a label, as a button that shows a tooltip on hover, keyboard focus
 * or tap. The button names itself with `label`, and describes itself with `text` whether or not
 * the tooltip is open.
 */
export function InfoTip({ label, text, className }: InfoTipProps) {
  const descriptionId = useId()
  const slotProps = useMemo(() => ({ tooltip: { sx: tooltipSx } }), [])

  return (
    <>
      {/* A short tap opens it, rather than MUI's long press, and leaves time to read it. */}
      <MuiTooltip title={text} slotProps={slotProps} enterTouchDelay={0} leaveTouchDelay={6000}>
        <button
          type="button"
          aria-label={label}
          aria-describedby={descriptionId}
          className={cn(targetSize, 'shrink-0 rounded-xs text-text-light', focusRing, className)}
        >
          <Info size={14} />
        </button>
      </MuiTooltip>
      <span id={descriptionId} hidden>
        {text}
      </span>
    </>
  )
}
